'use client'

import * as React from 'react'

import { Combobox as ComboboxPrimitive } from '@base-ui-components/react/combobox'
import { cva, type VariantProps } from 'class-variance-authority'
import { Check, ChevronDown, X } from 'lucide-react'

import { cn } from '@/utils'

// Define input size variants (without file: part)
const inputVariants = cva(
  `
    flex w-full bg-background border border-input shadow-xs shadow-black/5 transition-[color,box-shadow] text-foreground placeholder:text-muted-foreground/80 
    focus-visible:ring-ring/30 focus-visible:border-ring focus-visible:outline-none focus-visible:ring-[3px]     
    has-[[data-slot=combobox-input]:focus-visible]:ring-ring/30 
    has-[[data-slot=combobox-input]:focus-visible]:border-ring
    has-[[data-slot=combobox-input]:focus-visible]:outline-none
    has-[[data-slot=combobox-input]:focus-visible]:ring-[3px]
    [&_[data-slot=combobox-input]]:grow
    disabled:cursor-not-allowed disabled:opacity-60 
    [&[readonly]]:bg-muted/80 [&[readonly]]:cursor-not-allowed
    aria-invalid:border-destructive/60 aria-invalid:ring-destructive/10 dark:aria-invalid:border-destructive dark:aria-invalid:ring-destructive/20
  `,
  {
    defaultVariants: {
      variant: 'md'
    },
    variants: {
      variant: {
        lg: 'py-1 min-h-10 px-4 text-sm rounded-md [&~[data-slot=combobox-icon]]:end-2.5 [&~[data-slot=combobox-clear]]:end-7',
        md: 'py-1 min-h-9 px-3 text-sm rounded-md [&~[data-slot=combobox-icon]]:end-2 [&~[data-slot=combobox-clear]]:end-6',
        sm: 'py-0.5 min-h-8 px-2.5 text-xs rounded-md [&~[data-slot=combobox-icon]]:end-1.75 [&~[data-slot=combobox-clear]]:end-5.75'
      }
    }
  }
)

const chipsVariants = cva(
  [
    'flex items-center flex-wrap gap-1',
    '[&_[data-slot=combobox-input]]:py-0 [&_[data-slot=combobox-input]]:px-1.5 has-[[data-slot=combobox-chip]]:[&_[data-slot=combobox-input]]:px-0',
    '[&_[data-slot=combobox-input]]:min-h-0 [&_[data-slot=combobox-input]]:flex-1',
    '[&_[data-slot=combobox-input]]:border-0 [&_[data-slot=combobox-input]]:shadow-none [&_[data-slot=combobox-input]]:rounded-none',
    '[&_[data-slot=combobox-input]]:outline-none [&_[data-slot=combobox-input]]:ring-0'
  ],
  {
    variants: {
      variant: {
        lg: 'px-1.5',
        md: 'px-1',
        sm: 'px-0.75'
      }
    }
  }
)

// Root - Groups all parts of the combobox
function Combobox({
  ...props
}: React.ComponentProps<typeof ComboboxPrimitive.Root>) {
  return <ComboboxPrimitive.Root data-slot="combobox" {...props} />
}

// Arrow - Displays an element positioned against the combobox anchor
function ComboboxArrow({
  className,
  ...props
}: React.ComponentProps<typeof ComboboxPrimitive.Arrow>) {
  return (
    <ComboboxPrimitive.Arrow
      className={cn('', className)}
      data-slot="combobox-arrow"
      {...props}
    />
  )
}

// Backdrop - An overlay displayed beneath the combobox popup
function ComboboxBackdrop({
  ...props
}: React.ComponentProps<typeof ComboboxPrimitive.Backdrop>) {
  return <ComboboxPrimitive.Backdrop data-slot="combobox-backdrop" {...props} />
}

// Chip - An individual chip representing a selected item
function ComboboxChip({
  className,
  ...props
}: React.ComponentProps<typeof ComboboxPrimitive.Chip>) {
  return (
    <ComboboxPrimitive.Chip
      className={cn(
        'bg-muted text-foreground inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium rtl:flex-row-reverse',
        className
      )}
      data-slot="combobox-chip"
      {...props}
    />
  )
}

// ChipRemove - A button to remove a chip
function ComboboxChipRemove({
  children,
  className,
  ...props
}: React.ComponentProps<typeof ComboboxPrimitive.ChipRemove>) {
  return (
    <ComboboxPrimitive.ChipRemove
      className={cn(
        'hover:bg-muted-foreground/20 ms-1 cursor-pointer rounded-sm [&_svg]:opacity-60 hover:[&_svg]:opacity-100',
        className
      )}
      data-slot="combobox-chip-remove"
      {...props}
    >
      {children ? children : <X className="size-3.5" />}
    </ComboboxPrimitive.ChipRemove>
  )
}

// Chips - A container for selected items as chips (for multi-select)
function ComboboxChips({
  className,
  variant = 'md',
  ...props
}: React.ComponentProps<typeof ComboboxPrimitive.Chips> &
  VariantProps<typeof inputVariants>) {
  return (
    <ComboboxPrimitive.Chips
      className={cn(
        inputVariants({ variant }),
        chipsVariants({ variant }),
        className
      )}
      data-slot="combobox-chips"
      {...props}
    />
  )
}

// Clear - A button to clear the input value
function ComboboxClear({
  children,
  className,
  ...props
}: React.ComponentProps<typeof ComboboxPrimitive.Clear>) {
  return (
    <ComboboxPrimitive.Clear
      className={cn(
        'ring-offset-background absolute end-6 top-1/2 -translate-y-1/2 cursor-pointer rounded-sm opacity-70',
        'focus:ring-ring opacity-60 transition-opacity hover:opacity-100 focus:ring-2 focus:ring-offset-2 focus:outline-none disabled:pointer-events-none',
        'data-[disabled]:pointer-events-none',
        className
      )}
      data-slot="combobox-clear"
      {...props}
    >
      {children ? children : <X className="size-3.5 opacity-100" />}
    </ComboboxPrimitive.Clear>
  )
}

// Collection - A collection of combobox items
function ComboboxCollection({
  ...props
}: React.ComponentProps<typeof ComboboxPrimitive.Collection>) {
  return (
    <ComboboxPrimitive.Collection data-slot="combobox-collection" {...props} />
  )
}

function ComboboxContent({
  align = 'start',
  alignOffset = 0,
  anchor,
  children,
  className,
  showBackdrop = false,
  side = 'bottom',
  sideOffset = 4,
  ...props
}: {
  align?: ComboboxPrimitive.Positioner.Props['align']
  alignOffset?: ComboboxPrimitive.Positioner.Props['alignOffset']
  anchor?: ComboboxPrimitive.Positioner.Props['anchor']
  showBackdrop?: boolean
  side?: ComboboxPrimitive.Positioner.Props['side']
  sideOffset?: ComboboxPrimitive.Positioner.Props['sideOffset']
} & React.ComponentProps<typeof ComboboxPrimitive.Popup>) {
  return (
    <ComboboxPortal>
      {showBackdrop && <ComboboxBackdrop />}
      <ComboboxPositioner
        align={align}
        alignOffset={alignOffset}
        anchor={anchor}
        side={side}
        sideOffset={sideOffset}
      >
        <ComboboxPopup className={className} {...props}>
          {children}
        </ComboboxPopup>
      </ComboboxPositioner>
    </ComboboxPortal>
  )
}

// Input and Clear controls
function ComboboxControl({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <span
      className={cn('relative', className)}
      data-slot="combobox-control"
      {...props}
    />
  )
}

// Empty - A component to display when no options are available
function ComboboxEmpty({
  className,
  ...props
}: React.ComponentProps<typeof ComboboxPrimitive.Empty>) {
  return (
    <ComboboxPrimitive.Empty
      className={cn(
        'text-muted-foreground px-2 py-1.5 text-sm empty:m-0 empty:p-0',
        className
      )}
      data-slot="combobox-empty"
      {...props}
    />
  )
}

// Group - Groups related combobox items with the corresponding label
function ComboboxGroup({
  ...props
}: React.ComponentProps<typeof ComboboxPrimitive.Group>) {
  return <ComboboxPrimitive.Group data-slot="combobox-group" {...props} />
}

// GroupLabel - An accessible label that is automatically associated with its parent group
function ComboboxGroupLabel({
  className,
  ...props
}: React.ComponentProps<typeof ComboboxPrimitive.GroupLabel>) {
  return (
    <ComboboxPrimitive.GroupLabel
      className={cn(
        'text-muted-foreground px-2 py-1.5 text-xs font-medium',
        className
      )}
      data-slot="combobox-group-label"
      {...props}
    />
  )
}

// Icon - An icon element for the combobox
function ComboboxIcon({
  children,
  className,
  ...props
}: React.ComponentProps<typeof ComboboxPrimitive.Icon>) {
  return (
    <ComboboxPrimitive.Icon
      className={cn(
        'ring-offset-background absolute end-2 top-1/2 -translate-y-1/2 cursor-pointer rounded-sm opacity-70 transition-opacity',
        'focus:ring-ring opacity-60 focus:ring-2 focus:ring-offset-2 focus:outline-none disabled:pointer-events-none',
        'data-[disabled]:pointer-events-none',
        className
      )}
      data-slot="combobox-icon"
      {...props}
    >
      {children ? children : <ChevronDown className="size-3.5 opacity-100" />}
    </ComboboxPrimitive.Icon>
  )
}

// Input - The input element for typing
function ComboboxInput({
  className,
  variant = 'md',
  ...props
}: React.ComponentProps<typeof ComboboxPrimitive.Input> &
  VariantProps<typeof inputVariants>) {
  return (
    <ComboboxPrimitive.Input
      className={cn(inputVariants({ variant }), className)}
      data-slot="combobox-input"
      data-variant={variant}
      {...props}
    />
  )
}

// Item - An individual selectable option in the combobox
function ComboboxItem({
  className,
  onClick,
  onPointerDown,
  ...props
}: React.ComponentProps<typeof ComboboxPrimitive.Item>) {
  return (
    <ComboboxPrimitive.Item
      className={cn(
        'relative flex cursor-default items-center',
        'text-foreground relative items-center gap-2 rounded-md py-1.5 ps-7 pe-2 text-sm outline-hidden transition-colors select-none data-disabled:pointer-events-none data-disabled:opacity-50',
        '[&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*=size-])]:size-4 [&_svg:not([role=img]):not([class*=text-])]:opacity-60',
        'data-[highlighted]:text-foreground data-[highlighted]:before:bg-accent data-[highlighted]:relative data-[highlighted]:z-0 data-[highlighted]:before:absolute data-[highlighted]:before:inset-x-1 data-[highlighted]:before:inset-y-0 data-[highlighted]:before:z-[-1] data-[highlighted]:before:rounded-sm',
        'pointer-events-auto relative z-10',
        className
      )}
      data-slot="combobox-item"
      onClick={(e) => {
        // Prevent dialog from intercepting clicks on combobox items
        e.stopPropagation()
        onClick?.(e)
      }}
      onPointerDown={(e) => {
        // Prevent dialog from intercepting clicks on combobox items
        e.stopPropagation()
        onPointerDown?.(e)
      }}
      {...props}
    />
  )
}

// ItemIndicator - An indicator for selected items
function ComboboxItemIndicator({
  className,
  ...props
}: React.ComponentProps<typeof ComboboxPrimitive.ItemIndicator>) {
  return (
    <ComboboxPrimitive.ItemIndicator
      className={cn(
        'absolute start-2.5 top-1/2 flex -translate-y-1/2 items-center justify-center',
        className
      )}
      data-slot="combobox-item-indicator"
      {...props}
    >
      <Check className="text-primary h-4 w-4" />
    </ComboboxPrimitive.ItemIndicator>
  )
}

// List - A container for the combobox options
function ComboboxList({
  className,
  ...props
}: React.ComponentProps<typeof ComboboxPrimitive.List>) {
  return (
    <ComboboxPrimitive.List
      className={cn('pointer-events-auto relative z-10 space-y-0.5', className)}
      data-slot="combobox-list"
      {...props}
    />
  )
}

// Popup - A container for the combobox options
function ComboboxPopup({
  className,
  onClick,
  onPointerDown,
  ...props
}: React.ComponentProps<typeof ComboboxPrimitive.Popup>) {
  return (
    <ComboboxPrimitive.Popup
      className={cn(
        'max-h-[min(var(--available-height),23rem)] w-[var(--anchor-width)] max-w-[var(--available-width)] py-1',
        'scroll-pt-2 scroll-pb-2 overflow-y-auto overscroll-contain bg-[canvas]',
        'border-border bg-popover text-popover-foreground rounded-md border shadow-md shadow-black/5',
        'origin-[var(--transform-origin)] transition-[transform,scale,opacity] data-[ending-style]:scale-90',
        'data-[ending-style]:opacity-0 data-[starting-style]:scale-90 data-[starting-style]:opacity-0',
        'pointer-events-auto relative z-[9999]',
        className
      )}
      data-slot="combobox-popup"
      onClick={(e) => {
        // Prevent dialog from intercepting clicks and closing the combobox
        e.stopPropagation()
        onClick?.(e)
      }}
      onPointerDown={(e) => {
        // Prevent dialog from intercepting clicks and closing the combobox
        e.stopPropagation()
        onPointerDown?.(e)
      }}
      {...props}
    />
  )
}

// Portal - A portal element that moves the popup to a different part of the DOM
function ComboboxPortal({
  ...props
}: React.ComponentProps<typeof ComboboxPrimitive.Portal>) {
  return <ComboboxPrimitive.Portal data-slot="combobox-portal" {...props} />
}

// Positioner - Positions the combobox popup against the input
function ComboboxPositioner({
  className,
  onClick,
  onPointerDown,
  ...props
}: React.ComponentProps<typeof ComboboxPrimitive.Positioner>) {
  return (
    <ComboboxPrimitive.Positioner
      className={cn('pointer-events-auto z-[9999] outline-none', className)}
      data-slot="combobox-positioner"
      onClick={(e) => {
        // Prevent dialog from intercepting clicks on the positioner
        e.stopPropagation()
        onClick?.(e)
      }}
      onPointerDown={(e) => {
        // Prevent dialog from intercepting clicks on the positioner
        e.stopPropagation()
        onPointerDown?.(e)
      }}
      {...props}
    />
  )
}

// Row - A row container for combobox items
function ComboboxRow({
  className,
  ...props
}: React.ComponentProps<typeof ComboboxPrimitive.Row>) {
  return (
    <ComboboxPrimitive.Row
      className={cn('flex items-center gap-2 rtl:flex-row-reverse', className)}
      data-slot="combobox-row"
      {...props}
    />
  )
}

// Separator - A separator element accessible to screen readers
function ComboboxSeparator({
  className,
  ...props
}: React.ComponentProps<typeof ComboboxPrimitive.Separator>) {
  return (
    <ComboboxPrimitive.Separator
      className={cn('bg-muted my-1.5 h-px', className)}
      data-slot="combobox-separator"
      {...props}
    />
  )
}

// Status - Displays the status of the combobox
function ComboboxStatus({
  className,
  ...props
}: React.ComponentProps<typeof ComboboxPrimitive.Status>) {
  return (
    <ComboboxPrimitive.Status
      className={cn('text-muted-foreground text-sm', className)}
      data-slot="combobox-status"
      {...props}
    />
  )
}

// Trigger - A button that opens the combobox
function ComboboxTrigger({
  className,
  ...props
}: React.ComponentProps<typeof ComboboxPrimitive.Trigger>) {
  return (
    <ComboboxPrimitive.Trigger
      className={cn('relative', className)}
      data-slot="combobox-trigger"
      {...props}
    />
  )
}

// Value - Displays the selected value
function ComboboxValue({
  ...props
}: React.ComponentProps<typeof ComboboxPrimitive.Value>) {
  return <ComboboxPrimitive.Value data-slot="combobox-value" {...props} />
}

export {
  Combobox,
  ComboboxArrow,
  ComboboxBackdrop,
  ComboboxChip,
  ComboboxChipRemove,
  ComboboxChips,
  ComboboxClear,
  ComboboxCollection,
  ComboboxContent,
  ComboboxControl,
  ComboboxEmpty,
  ComboboxGroup,
  ComboboxGroupLabel,
  ComboboxIcon,
  ComboboxInput,
  ComboboxItem,
  ComboboxItemIndicator,
  ComboboxList,
  ComboboxPopup,
  ComboboxPortal,
  ComboboxPositioner,
  ComboboxRow,
  ComboboxSeparator,
  ComboboxStatus,
  ComboboxTrigger,
  ComboboxValue
}
